import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import FeedbackModal from './FeedbackModal';
import { captureScreenshot } from '../utils/captureScreenshot';
import { isDraftSurface } from '../utils/isDraftSurface';
import { isShellPath } from '../shell/shellRoutes';

interface FeedbackButtonProps {
  searchContext?: Record<string, unknown>;
  /** `floating` is the App-level pill (off the shell paths); `header` lives in the shell header. */
  variant?: 'floating' | 'header';
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({ searchContext, variant = 'floating' }) => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [capturedScreenshot, setCapturedScreenshot] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [screenshotSuppressed, setScreenshotSuppressed] = useState(false);

  // Capture the screenshot BEFORE the modal mounts so the modal itself
  // doesn't appear in the image. Capture is best-effort: a null result
  // simply means no screenshot will be attached.
  //
  // Draft surfaces are the exception: never capture at all. A screenshot
  // would put a customer's proprietary, unreleased draft label content
  // into the SHARED cross-org feedback-screenshot bucket, which Notion and
  // other consumers can read via signed URLs — that's egress RLS can't
  // touch, since it never goes through the database (spec §5.10).
  const handleClick = async () => {
    if (isCapturing || isModalOpen) return;
    // TODO(tenancy §5.10): route-based detection fails OPEN — /research/:jobId
    // (label_negotiations corpus) and the version-comparison modal still get
    // captured. Replace with a fail-closed DraftContentBoundary context that
    // draft-rendering components set. See isDraftSurface.ts.
    if (isDraftSurface(location.pathname, location.search)) {
      setCapturedScreenshot(null);
      setScreenshotSuppressed(true);
      setIsModalOpen(true);
      return;
    }
    setScreenshotSuppressed(false);
    setIsCapturing(true);
    try {
      const dataUrl = await captureScreenshot();
      setCapturedScreenshot(dataUrl);
    } finally {
      setIsCapturing(false);
      setIsModalOpen(true);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setCapturedScreenshot(null);
    setScreenshotSuppressed(false);
  };

  // After every hook: the shell header carries Feedback on shell paths, so the
  // floating pill would be a second one.
  if (variant === 'floating' && isShellPath(location.pathname + location.search)) return null;

  const trigger = variant === 'header' ? (
    <button
      onClick={handleClick}
      disabled={isCapturing}
      data-feedback-skip
      className="text-[13px] text-ink-2 hover:text-ink disabled:opacity-60 disabled:cursor-wait"
      title="Send Feedback"
    >
      {isCapturing ? 'Capturing…' : 'Feedback'}
    </button>
  ) : (
      <button
        onClick={handleClick}
        disabled={isCapturing}
        data-feedback-skip
        className="fixed bottom-3 right-6 z-40 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full shadow-lg hover:shadow-xl hover:border-indigo-300 transition-all duration-200 group disabled:opacity-60 disabled:cursor-wait"
        title="Send Feedback"
      >
        <svg
          className="w-3.5 h-3.5 text-slate-500 group-hover:text-indigo-600 transition-colors"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <span className="text-xs font-medium text-slate-600 group-hover:text-indigo-700 transition-colors">
          {isCapturing ? 'Capturing…' : 'Feedback'}
        </span>
      </button>
  );

  return (
    <>
      {trigger}

      <FeedbackModal
        isOpen={isModalOpen}
        onClose={handleClose}
        searchContext={searchContext}
        screenshotDataUrl={capturedScreenshot}
        screenshotSuppressed={screenshotSuppressed}
      />
    </>
  );
};

export default FeedbackButton;
